import { Controller } from '@hotwired/stimulus'
import {
  CURRENCY_SIMBOL,
  FormatCurrency,
  getFormattedDate,
  monthNames,
} from 'critical/calculator-helper'
import {
  calculateTotalInterestAndPeriod,
  calculateMinimumRepayment,
} from 'rvu-mortgages-calculations'
import { formatNumber } from '../../components/component/calculators/utils/helper'

export default class extends Controller<HTMLFormElement> {
  static override targets = [
    'repaying',
    'amount',
    'dueDate',
    'amountInput',
    'aprInput',
    'balanceInput',
    'monthInput',
    'feeInput',
    'fee',
    'totalFees',
    'savings',
    'months',
    'payOffBy',
    'errors',
  ]

  declare readonly repayingTarget: HTMLInputElement
  declare readonly amountTarget: HTMLInputElement
  declare readonly dueDateTarget: HTMLInputElement
  declare readonly amountInputTarget: HTMLInputElement
  declare readonly aprInputTarget: HTMLInputElement
  declare readonly balanceInputTarget: HTMLInputElement
  declare readonly monthInputTarget: HTMLInputElement
  declare readonly feeInputTarget: HTMLInputElement
  declare readonly feeTarget: HTMLInputElement
  declare readonly totalFeesTarget: HTMLInputElement
  declare readonly savingsTarget: HTMLInputElement
  declare readonly monthsTarget: HTMLInputElement
  declare readonly payOffByTarget: HTMLInputElement
  declare readonly errorsTarget: HTMLInputElement

  calculateBalanceTransferFee = (
    balance: string,
    fee: string,
    amount: string,
    months: string,
    apr: string,
  ) => {
    const newBalance =
      formatNumber(balance) * (1 + parseFloat(fee) / 100) - formatNumber(amount) * parseInt(months)

    if (newBalance >= 0) {
      const BtResult = calculateTotalInterestAndPeriod(newBalance, apr, formatNumber(amount))
      return {
        cardOffBalance: BtResult.total + +months * +amount,
        dueDatePeriod: getFormattedDate(BtResult.period + +months),
      }
    } else {
      const btTotal = formatNumber(balance) * (1 + parseFloat(fee) / 100)
      return {
        cardOffBalance: btTotal,
        dueDatePeriod: getFormattedDate(btTotal / formatNumber(amount)),
      }
    }
  }

  updateUI = (
    cardOffBalance: string,
    balance: string,
    result: any,
    dueDatePeriod: Date,
    months: string,
    fee: string,
  ) => {
    const totalfees = Math.ceil(parseInt(cardOffBalance) - parseFloat(balance.replace(/,/g, '')))
    this.totalFeesTarget.textContent = `£${totalfees}`

    this.savingsTarget.textContent = `£${FormatCurrency(
      Math.ceil(+result.total - +cardOffBalance),
    )}`

    this.payOffByTarget.textContent = `${
      monthNames[dueDatePeriod.getMonth()]
    } ${dueDatePeriod.getFullYear()}`

    this.monthsTarget.textContent = `${months} months`
    this.feeTarget.textContent = `${fee}`
  }

  submit(e?: any) {
    e?.preventDefault()
    const apr = this.aprInputTarget.value
    const balance = this.balanceInputTarget.value
    const amount = this.amountInputTarget.value
    const fee = this.feeInputTarget.value
    const months = this.monthInputTarget.value

    const result = calculateTotalInterestAndPeriod(
      parseFloat(balance.replace(/,/g, '')),
      apr,
      parseFloat(amount.replace(/,/g, '')),
    )

    if (result.error) {
      const errorFun = calculateMinimumRepayment(formatNumber(balance), apr, 1, 5)

      this.errorsTarget.textContent = `Please ensure monthly payments are more than £${errorFun}.`
    } else {
      this.errorsTarget.textContent = ''
    }

    const formattedDate = getFormattedDate(result.period)

    this.repayingTarget.textContent = ` ${result.period} months`
    this.amountTarget.textContent = `  ${CURRENCY_SIMBOL}${FormatCurrency(
      +result.total,
      'GBP',
      CURRENCY_SIMBOL,
    )}`

    this.dueDateTarget.textContent = `${
      monthNames[formattedDate.getMonth()]
    } ${formattedDate.getFullYear()}`

    const BTresult = this.calculateBalanceTransferFee(balance, fee, amount, months, apr)
    const dueDatePeriod = BTresult.dueDatePeriod
    const cardOffBalance = BTresult.cardOffBalance

    this.updateUI(cardOffBalance, balance, result, dueDatePeriod, months, fee)
  }

  changeCalcInput() {
    const apr = this.aprInputTarget.value
    const balance = this.balanceInputTarget.value
    const amount = this.amountInputTarget.value
    const fee = this.feeInputTarget.value
    const months = this.monthInputTarget.value

    const result = calculateTotalInterestAndPeriod(
      parseFloat(balance.replace(/,/g, '')),
      apr,
      parseFloat(amount.replace(/,/g, '')),
    )

    const BTresult = this.calculateBalanceTransferFee(balance, fee, amount, months, apr)
    const dueDatePeriod = BTresult.dueDatePeriod
    const cardOffBalance = BTresult.cardOffBalance

    this.updateUI(cardOffBalance, balance, result, dueDatePeriod, months, fee)
  }

  override connect() {
    const urlSearchParams = new URLSearchParams(window.location.search)
    // @ts-ignore
    const params = Object.fromEntries(urlSearchParams.entries())
    const { apr, amount, balance } = params

    if (apr && amount && balance) {
      this.balanceInputTarget.value = balance
      this.aprInputTarget.value = apr
      this.amountInputTarget.value = amount

      this.submit()
    }
  }
}
